import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, Skeleton, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/EditOutlined';
import { getCurrentExchangeRate, getExchangeRateOptions } from '../../api/exchangeRate';
import { useAuth } from '../../context/authContext';
import { formatRate, formatRateDateTime } from '../../utils/exchangeRates';
import { EditExchangeRateDialog } from './EditExchangeRateDialog';

const STALE_THRESHOLD_HOURS = 48;

/** SSCL tax / exchange-rate payment overhaul (Phase 79) — shown on the
 * Student Payments tab next to the Payment Checklist. There is deliberately
 * no "Refresh" trigger here — the actual Nations Trust fetch runs on a
 * separate external machine the user controls (direct server-side fetches
 * from this host are blocked by Nations Trust's own CloudFront WAF), which
 * pushes verified rates into this app on its own schedule (see
 * scripts/exchange-rate-fetcher). This panel only ever shows what's actually
 * been received — never a fabricated or guessed rate.
 *
 * Phase 104: also shows the latest manually published rate (effective date,
 * publisher, reason), and gives Super Admin and finance users with
 * exchange_rates:manage an "Edit Exchange Rate" action to publish one. The
 * automatic rate stays the current one either way. */
export const ExchangeRatePanel = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('exchange_rates', 'manage');
  const [editOpen, setEditOpen] = useState(false);
  const query = useQuery({ queryKey: ['exchange-rate-current'], queryFn: getCurrentExchangeRate });
  const options = useQuery({ queryKey: ['exchange-rate-options'], queryFn: getExchangeRateOptions });
  const latestManual = options.data?.manual?.[0] ?? null;

  if (query.isLoading) return <Skeleton variant="rounded" height={48} sx={{ mb: 1 }} />;

  const rate = query.data ?? null;
  const ageHours = rate ? (Date.now() - new Date(rate.receivedAt).getTime()) / (1000 * 60 * 60) : 0;
  const isStale = ageHours > STALE_THRESHOLD_HOURS;

  return (
    <Box sx={{ mb: 1 }}>
      {rate ? (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="baseline">
          <Typography variant="body2">
            <strong>USD/LKR Exchange Rate:</strong> Rs. {Number(rate.rate).toFixed(2)}
          </Typography>
          <Chip size="small" label="Automatic" color="primary" variant="outlined" />
          <Typography variant="body2" color="text.secondary">
            Rate Date: {formatRateDateTime(rate.sourcePublishedAt)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Source: {rate.source} — Telegraphic Transfers Selling Rate
          </Typography>
          <Typography variant="caption" color={isStale ? 'warning.main' : 'text.secondary'}>
            Last successful update: {formatRateDateTime(rate.receivedAt)}
            {isStale ? ' (overdue — check the exchange-rate fetcher)' : ''}
          </Typography>
        </Stack>
      ) : (
        <Alert severity="warning" sx={{ mb: 1 }}>
          No USD/LKR exchange rate has been received yet. Taxable fee items will show "Rate unavailable" until one arrives.
        </Alert>
      )}
      {(latestManual || canManage) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          sx={{ mt: 0.5 }}
        >
          <Typography variant="body2" color="text.secondary" component="div" data-testid="latest-manual-rate">
            {latestManual ? (
              <>
                <strong>Latest manual rate:</strong> Rs. {formatRate(latestManual.rate)}{' '}
                <Chip size="small" label="Manual" color="secondary" variant="outlined" sx={{ mx: 0.5 }} />
                effective {formatRateDateTime(latestManual.sourcePublishedAt)}
                {latestManual.publishedBy
                  ? ` · by ${latestManual.publishedBy.firstName} ${latestManual.publishedBy.lastName}`
                  : ''}
                {latestManual.reason ? ` · ${latestManual.reason}` : ''}
                {' — used only where selected.'}
              </>
            ) : (
              'No manual rate has been published.'
            )}
          </Typography>
          {canManage && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setEditOpen(true)}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Edit Exchange Rate
            </Button>
          )}
        </Stack>
      )}
      {canManage && <EditExchangeRateDialog open={editOpen} onClose={() => setEditOpen(false)} currentRate={rate} />}
    </Box>
  );
};
